"use client";

import { Header, Footer } from "@/components/common";

export default function DPAClient() {
  return (
    <div className="min-h-screen bg-[#212121]">
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-[#212121]">
        <div className="container mx-auto px-6 max-w-6xl">
          <h1 className="text-4xl lg:text-5xl font-medium tracking-tight text-[#F8F7F4] mb-4">
            Data Processing Addendum
          </h1>
          <p className="text-lg text-[#A4A4A4]">
            Our commitment to GDPR compliance and data protection standards.
          </p>
          <p className="text-sm text-[#909090] mt-4">
            Effective: 31 March 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="container mx-auto px-6 max-w-6xl pb-24">
        <div className="space-y-12">

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Definitions</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">1.1 Agreement</span> — Shop Circle Holdings Ltd (trading as Keystone) Terms and Conditions and Privacy Policy, or other written or electronic agreement governing the provision of services.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.2 Customer Data</span> — Any personal data that Keystone processes on Customer&apos;s behalf.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.3 Data Protection Laws</span> — All applicable worldwide legislation on data protection and privacy, including CCPA and laws from Canada, Australia, and Brazil.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.4(a) European Data Protection Laws</span> — Data protection laws applicable to Europe, including the EU GDPR (Regulation (EU) 2016/679) and the UK GDPR (the EU GDPR as retained in UK law by the European Union (Withdrawal) Act 2018, as amended by the Data Protection, Privacy and Electronic Communications (Amendments etc) (EU Exit) Regulations 2019).
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.4(b) CCPA/CPRA</span> — California Consumer Privacy Act and California Privacy Rights Act, as amended.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.5 Europe</span> — EEA member states, Switzerland, and the United Kingdom.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.6</span> — &quot;Personal data,&quot; &quot;controller,&quot; &quot;data subject,&quot; &quot;processor,&quot; and &quot;processing&quot; carry the meanings defined under applicable Data Protection Laws.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.7(a) Data Protection Laws</span> — Binding privacy laws including GDPR, UK GDPR, FADP, CCPA/CPRA, Virginia and Colorado Privacy Acts.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.7(b) Sensitive Data</span> — Special categories including social security numbers, genetic/biometric/health information, racial/ethnic/political/religious affiliation, and criminal records.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.8 Sub-processor</span> — Any processor engaged by Keystone to assist with service obligations.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">1.9 Security Incident</span> — An unauthorized breach causing destruction, loss, alteration, or unauthorized disclosure of Customer Data.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Roles and Responsibilities */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Roles and Responsibilities</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">2.1</span> — Under European Data Protection Laws, Keystone acts as processor on Customer&apos;s behalf. Under UK Data Protection Laws, Keystone also acts as processor on Customer&apos;s behalf. In relation to certain analytics and usage data described in the Privacy Policy, Keystone may act as an independent controller.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">2.2</span> — Keystone processes Customer Data per Annex A. Customer shall not provide Sensitive Data; Keystone has no liability for Sensitive Data.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">2.3</span> — Customer ensures Keystone&apos;s processing complies with applicable law. Where Customer acts as processor, Customer warrants authorization from third-party controllers.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Sub-Processing */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Sub-Processing</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">3.1</span> — Keystone may engage Sub-processors. Currently authorized Sub-processors:
                <ul className="space-y-2 text-base text-[#A4A4A4] list-disc pl-5 mt-2">
                  <li>(a) Shopify Inc. (Canada) — E-commerce platform provider and source of customer/order data;</li>
                  <li>(b) Postmark (ActiveCampaign, LLC, USA) — Transactional email delivery for customer communications;</li>
                  <li>(c) Ortto (Ortto Inc., USA) — Transactional marketing emails towards merchants;</li>
                  <li>(d) Amplitude (Amplitude, Inc., USA) — Usage analytics (IP address, location data);</li>
                  <li>(e) Klaviyo (Klaviyo, Inc., USA) — Third-party integration for transactional customer emails;</li>
                  <li>(f) Omnisend (Omnisend Ltd., UK) — Third-party integration for transactional customer emails;</li>
                  <li>(g) Mailchimp (The Rocket Science Group LLC, USA) — Third-party integration for transactional customer emails.</li>
                </ul>
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">3.2</span> — Keystone enters written agreements with Sub-processors containing data protection obligations no less protective than this Addendum, per Data Protection Laws. Keystone is responsible for Sub-processor acts and omissions.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">3.3</span> — Keystone informs Customer of new Sub-processors by updating the list. Customer has 30 days to object if there is a material adverse effect on compliance.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">3.4</span> — Keystone may be prevented from disclosing Sub-processor agreements due to confidentiality but shall provide relevant information upon request.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Security */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Security</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">4.1</span> — Keystone implements appropriate technical and organizational security measures protecting Customer Data from Security Incidents.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">4.2</span> — Keystone ensures authorized persons maintaining confidentiality obligations (contractual or statutory).
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">4.3</span> — Keystone implements appropriate measures including physical security and regular backups.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">4.4</span> — Upon Security Incident awareness, Keystone shall: (a) notify Customer without undue delay, no later than 48 hours; (b) provide timely information; (c) promptly contain and investigate. Notification does not constitute fault acknowledgment.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">4.5</span> — Customer is responsible for secure service use, including credential protection and data encryption.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Security Reports */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Security Reports</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">5.1</span> — Keystone makes available information demonstrating compliance with this Addendum.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">5.2</span> — Upon Customer&apos;s written request and subject to reasonable confidentiality obligations, Keystone shall make available information necessary to demonstrate compliance with this Addendum and shall allow for and contribute to audits, including inspections, conducted by Customer or a third-party auditor mandated by Customer. Such audits shall be limited to once per twelve (12) month period, conducted during normal business hours with at least thirty (30) days&apos; prior written notice, and at Customer&apos;s expense.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">International Transfers</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">6.1</span> — Keystone&apos;s primary application data servers are deployed in Frankfurt, Germany (European Union). Customer Data is primarily stored and processed within the European Economic Area.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">6.2</span> — Where Customer Data is transferred to Sub-processors located outside the European Economic Area or the United Kingdom, Keystone ensures appropriate safeguards are in place in accordance with applicable Data Protection Laws, including the use of Standard Contractual Clauses approved by the European Commission (Decision 2021/914) and/or the UK International Data Transfer Addendum issued by the ICO under Section 119A of the Data Protection Act 2018.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">6.3</span> — Keystone shall inform Customer of the legal basis for any transfer of Customer Data to a third country and of the appropriate safeguards taken.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Deletion of Data */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Deletion of Data</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">7.1</span> — Upon Agreement termination or expiration, Keystone deletes or returns Customer Data except where legally required retention applies.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">7.2</span> — Upon Customer deletion requests via Shopify webhooks, Keystone confirms receipt and completes action within 30 days (unless legally required retention applies). Implemented webhooks: Data Requests, Customer Data Redaction, Shop Data Redaction.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Data Subject Rights and Cooperation */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Data Subject Rights and Cooperation</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">8.1</span> — Keystone provides reasonable assistance enabling Customer compliance with data subject rights under Data Protection Laws. Keystone does not respond directly to data subject requests without Customer authorization, except where legally required.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">8.2</span> — Keystone provides reasonably requested information regarding the Service enabling Customer to conduct data protection impact assessments and prior consultations.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">8.3</span> — Keystone does not voluntarily provide government agencies Customer Data access. For compulsory requests from government agencies for Customers in Europe, Keystone shall: (a) review legality; (b) inform agency of processor status; (c) attempt redirecting to Customer; (d) notify Customer via email allowing protective order pursuit; (e) provide minimum permissible information.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">8.4 CCPA/CPRA Standard</span> — Processor does not receive Personal Information as service consideration. Processor shall not derive rights or benefits regarding Personal Information, nor combine it with other parties&apos; information. Processor uses and discloses Personal Information solely for specified purposes. Processor refrains from selling or sharing Personal Information without Customer&apos;s written consent.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* General */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">General</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">9.1</span> — Claims under this Addendum may be brought solely by the Agreement party Customer entity.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">9.2</span> — This Addendum remains effective during Keystone&apos;s Customer Data processing or until Agreement termination.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">9.3</span> — Upon conflict between this Addendum and the Agreement, this Addendum prevails.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">9.4</span> — Shop Circle Holdings Ltd reserves the right to update or modify this Addendum from time to time. Continued use of the Services following any such update constitutes acceptance of the modified terms.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">9.5</span> — This Addendum shall be governed by and construed in accordance with the laws of England and Wales. The courts of England and Wales shall have non-exclusive jurisdiction over any disputes arising under this Addendum, without prejudice to any rights of data subjects under applicable Data Protection Laws.
              </li>
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Annex A */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">Annex A</h2>

            <ul className="space-y-4 text-base text-[#A4A4A4]">
              <li>
                <span className="text-[#F8F7F4] font-medium">Categories of Data Subjects</span> — Customer&apos;s end users (including the customers of the merchant&apos;s online store) and merchants (store owners) who use the Keystone applications.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Categories of Personal Data</span> — Contact information (name, email address, phone number, WhatsApp); order and checkout data (order ID, products ordered, quantities, discounts, taxes, shipping, custom properties); device and usage information (IP address, browser type, device info, session behaviour); location data (country, city, country code); marketing preferences (newsletter opt-ins, campaign data); app usage analytics (feature usage, dashboard interactions); cookie data (session ID, CSRF token, Shopify OAuth); and customer birthdays (date of birth, where provided for loyalty rewards). The specific categories processed depend on which Keystone application(s) the Customer uses.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Frequency of Processing</span> — Continuous and Customer-determined.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Subject Matter/Nature of Processing</span> — Storage and processing necessary for providing, maintaining, and improving the service; disclosures per Agreement or legal compulsion.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Purpose of Processing</span> — (a) Providing service per Agreement; (b) Customer-initiated processing; (c) reasonable Customer instructions consistent with Agreement.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Duration/Retention Period</span> — Per Section 7.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Sensitive Data</span> — Not applicable. Keystone does not intentionally process special category data as defined under Article 9 of the EU GDPR or equivalent provisions under UK GDPR.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Data Server Location</span> — Primary servers: Frankfurt, Germany (EU). Sub-processors may process data in the USA, Canada, and United Kingdom as described in Section 3 and Section 6.
              </li>
              <li>
                <span className="text-[#F8F7F4] font-medium">Transfer Mechanisms</span> — EU Standard Contractual Clauses (Commission Implementing Decision (EU) 2021/914) and UK International Data Transfer Addendum (issued under Section 119A of the Data Protection Act 2018).
              </li>
            </ul>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
